/* eslint-disable react-hooks/rules-of-hooks */
import { Button } from '@efexteam/components';
import { RoleId, RoleIds } from '@efexteam/permission-manager';
import { Card, CardContent, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../components/common/Form/Input';
import { COUNTRIES_OPTIONS } from '../../components/common/Form/config';
import useBoolean from '../../hooks/useBoolean';
import { ICompanyDetails } from '../../services/auth/types';
import { displayValue } from '../../utils/formater';

interface CompanyDetailsProps {
  company?: ICompanyDetails;
  loading: boolean;
  onSubmit: (data: ICompanyDetails, blockEdited: 'company') => void;
  roleId?: RoleId;
  userType: string | undefined;
}

type CompanyKeys = keyof ICompanyDetails;

const INITIAL_STATE: ICompanyDetails = {
  email: '',
};

const CompanyDetails = ({
  company,
  loading,
  onSubmit,
  roleId,
  userType,
}: CompanyDetailsProps) => {
  const { t } = useTranslation();
  const [isEditActive, { toggle: toggleIsEditState }] = useBoolean(false);
  const [form, setForm] = useState<ICompanyDetails>(INITIAL_STATE);
  const country = COUNTRIES_OPTIONS.find(
    (option) => option.id === company?.country?.toLocaleLowerCase()
  );

  const handleChange = (event: {
    target: { name: CompanyKeys; value: string };
  }) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleEdit = () => {
    if (isEditActive && form) {
      onSubmit(form, 'company');
    }
    toggleIsEditState();
  };

  useEffect(() => {
    if (company) {
      setForm(company);
    }
  }, [company]);

  if (!company) {
    return null;
  }

  if (loading) {
    return <Skeleton variant="rectangular" height={100} />;
  }

  return (
    <Card className="my-4" title={t('companyDetails') || ''}>
      <CardContent>
        <div className="flex flex-wrap justify-between">
          <div className="w-1/2 md:w-1/4 mb-2">
            <p className="text-xs text-label-light mb-2">{t('businessName')}</p>

            {!isEditActive ? (
              <p className="text-sm">{displayValue(company.businessName)}</p>
            ) : (
              <Input
                autoFocus
                name="businessName"
                defaultValue={company.businessName}
                className="text-sm mb-2"
                onChange={(event: any) => handleChange(event)}
              />
            )}
          </div>
          <div className="w-1/2 md:w-1/4 mb-2">
            <p className="text-xs text-label-light mb-2">{t('address')}</p>

            {!isEditActive ? (
              <p className="text-sm">{displayValue(company.address)}</p>
            ) : (
              <Input
                name="address"
                defaultValue={company.address}
                className="text-sm mb-2"
                onChange={(event: any) => handleChange(event)}
              />
            )}
          </div>
          {company.cityState && !company.city && (
            <div className="w-1/2 md:w-1/4 mb-2">
              <p className="text-xs text-label-light mb-2">{t('city')}</p>
              {!isEditActive ? (
                <p className="text-sm">{displayValue(company.cityState)}</p>
              ) : (
                <Input
                  className="text-sm mb-2"
                  name="cityState"
                  defaultValue={company.cityState}
                  onChange={(event: any) => handleChange(event)}
                />
              )}
            </div>
          )}
          {company.city && (
            <div className="w-1/2 md:w-1/4 mb-2">
              <p className="text-xs text-label-light mb-2">{t('city')}</p>
              {!isEditActive ? (
                <p className="text-sm">{displayValue(company.city)}</p>
              ) : (
                <Input
                  className="text-sm mb-2"
                  name="city"
                  defaultValue={company.city}
                  onChange={(event: any) => handleChange(event)}
                />
              )}
            </div>
          )}
          {company.state && (
            <div className="w-1/2 md:w-1/4 mb-2">
              <p className="text-xs text-label-light mb-2">{t('state')}</p>
              {!isEditActive ? (
                <p className="text-sm">{displayValue(company.state)}</p>
              ) : (
                <Input
                  className="text-sm mb-2"
                  name="state"
                  defaultValue={company.state}
                  onChange={(event: any) => handleChange(event)}
                />
              )}
            </div>
          )}
          <div className="w-1/2 md:w-1/4">
            <p className="text-xs text-label-light mb-2">{t('country')}</p>
            <div className="p-1.5 rounded-none bg-primary-fill flex items-center">
              {country?.icon || COUNTRIES_OPTIONS[0].icon}
              <p className="m-0 text-sm">
                {country?.value || COUNTRIES_OPTIONS[0].value}
              </p>
            </div>
          </div>

          <div className="w-1/2 md:w-1/4 px-1 mb-2">
            <p className="text-xs text-label-light mb-2">
              {t(`email`) as string}
            </p>
            {!isEditActive ? (
              <p className="text-sm break-all">{displayValue(form?.email)}</p>
            ) : (
              <Input
                name="email"
                defaultValue={company?.email}
                className="text-sm mb-2"
                disabled
                readOnly
              />
            )}
          </div>

          {userType === 'COMPANY' && (
            <div className="w-1/2 md:w-1/4 mb-2">
              <p className="text-xs text-label-light mb-2">
                {t(`profilePhoneNumber`) as string}
              </p>
              {!isEditActive ? (
                <p className="text-sm">{displayValue(company.phoneNumber)}</p>
              ) : (
                <Input
                  name="phoneNumber"
                  defaultValue={company.phoneNumber}
                  className="text-sm mb-2"
                  onChange={(event: any) => handleChange(event)}
                />
              )}
            </div>
          )}
          <div className="w-1/2 md:w-1/4 mb-2">
            <p className="text-xs text-label-light mb-1">
              {t(`profileUserName${country?.value}`) as string}
            </p>
            <span className="text-[14px] break-all">{company.username}</span>
          </div>

          {userType === 'COMPANY' ? (
            <div className="w-full">
              <p className="text-xs text-label-light mb-2">
                {t(`profileWebsite`) as string}
              </p>
              {!isEditActive ? (
                <p className="text-sm">{displayValue(company.website)}</p>
              ) : (
                <Input
                  name="website"
                  defaultValue={company.website}
                  className="text-sm mb-2"
                  onChange={(event: any) => handleChange(event)}
                />
              )}
            </div>
          ) : null}
          {roleId === RoleIds.OWNER && (
            <div className="w-1/2 md:w-2/12 text-right !hidden">
              <Button
                onClick={handleEdit}
                className="w-full md:max-w-[120px] text-sm mt-2"
                appearance="secondary"
              >
                {isEditActive ? t('update') : t('edit')}
              </Button>
              {isEditActive && (
                <Button
                  onClick={toggleIsEditState}
                  className="w-full md:max-w-[120px] text-sm mt-2"
                  appearance="primary"
                >
                  {t('close')}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
export default CompanyDetails;
